import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Select, Space, Spin, Alert, Tag, Button, Row, Col, Card, Statistic } from 'antd';
import { IconShieldLock, IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminDeposits,
  getAdminDepositSummary,
  IAdminDepositRow,
  IAdminDepositSummary,
} from '../../../../services/AdminService';
import EmptyState from '../../../../components/EmptyState';
import AdminResponsiveList from '../../../../components/Admin/AdminResponsiveList';
import BookingMoneyDrawer from '../../../../components/Admin/BookingMoneyDrawer';
import {
  DEPOSIT_STATE_OPTIONS,
  DEPOSIT_STATE_LABEL_KEYS,
  DEPOSIT_COVERAGE_LABEL_KEYS,
  DEPOSIT_COVERAGE_COLORS,
} from '../../../../constants/adminMoneyOptions';
import { CORAL } from '../../../../theme/creamyTokens';
import { TRANSLATIONS } from '../../../../constants/translations';
import { translate } from '../../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter } from '../../../../helpers/adminTableHelpers';

const { Text } = Typography;

/**
 * The guests' fianzas -- money that is never the platform's, and the part of
 * this system that lies most easily.
 *
 * A card authorisation dies at seven days and the hold goes on 48 hours
 * before the rental starts, so on anything longer than about five days the
 * machine is out there against a hold that has already lapsed. The two
 * counters that matter here are exactly that: holds about to lapse
 * mid-rental, and holds that already have.
 */
const DepositsTab: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<IAdminDepositRow[]>([]);
  const [summary, setSummary] = useState<IAdminDepositSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [state, setState] = useState<string | undefined>(undefined);
  const [moneyBookingId, setMoneyBookingId] = useState<string | undefined>(undefined);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    Promise.all([getAdminDeposits(state), getAdminDepositSummary()])
      .then(([loadedRows, loadedSummary]) => {
        setRows(loadedRows);
        setSummary(loadedSummary);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [state]);

  useEffect(() => {
    load();
  }, [load]);

  const renderCoverage = (row: IAdminDepositRow) => (
    <Tag color={DEPOSIT_COVERAGE_COLORS[row.coverage]} style={{ marginInlineEnd: 0 }}>
      {translate(t, DEPOSIT_COVERAGE_LABEL_KEYS[row.coverage] ?? row.coverage)}
    </Tag>
  );

  const renderExpiry = (row: IAdminDepositRow) =>
    row.holdExpiresAt ? (
      <Text style={{ color: row.holdExpired ? CORAL : undefined, fontSize: 13 }}>
        {new Date(row.holdExpiresAt).toLocaleString()}
      </Text>
    ) : (
      '—'
    );

  const renderActions = (row: IAdminDepositRow) => (
    <Button size="small" onClick={() => setMoneyBookingId(row.bookingId)}>
      {translate(t, TRANSLATIONS.ADMIN_BOOKINGS_MONEY_BUTTON)}
    </Button>
  );

  return (
    <div>
      {summary && (
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          <Col xs={12} lg={8}>
            <Card size="small">
              <Statistic title={translate(t, TRANSLATIONS.ADMIN_DEPOSITS_HELD_NOW)} value={summary.heldNow} precision={2} suffix="€" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {summary.heldCount}
              </Text>
            </Card>
          </Col>
          <Col xs={12} lg={8}>
            <Card size="small">
              <Statistic
                title={translate(t, TRANSLATIONS.ADMIN_DEPOSITS_EXPIRING)}
                value={summary.expiringSoonCount}
                valueStyle={summary.expiringSoonCount > 0 ? { color: '#d48806' } : undefined}
              />
            </Card>
          </Col>
          <Col xs={12} lg={8}>
            {/* Not a warning -- a fact. These machines are out there with
                nothing frozen behind them right now. */}
            <Card size="small">
              <Statistic
                title={translate(t, TRANSLATIONS.ADMIN_DEPOSITS_EXPIRED)}
                value={summary.expiredWhileRunningCount}
                valueStyle={summary.expiredWhileRunningCount > 0 ? { color: CORAL } : undefined}
              />
            </Card>
          </Col>
          <Col xs={12} lg={8}>
            <Card size="small">
              <Statistic
                title={translate(t, TRANSLATIONS.ADMIN_DEPOSITS_FAILED)}
                value={summary.authorizationFailedCount}
                valueStyle={summary.authorizationFailedCount > 0 ? { color: CORAL } : undefined}
              />
            </Card>
          </Col>
          <Col xs={12} lg={8}>
            <Card size="small">
              <Statistic
                title={translate(t, TRANSLATIONS.ADMIN_DEPOSITS_CAPTURED)}
                value={summary.capturedTotal}
                precision={2}
                suffix="€"
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {summary.capturedCount}
              </Text>
            </Card>
          </Col>
        </Row>
      )}

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_DEPOSITS_STATE_FILTER_ALL)}
          style={{ maxWidth: 240, width: '100%' }}
          value={state}
          onChange={setState}
          options={DEPOSIT_STATE_OPTIONS.map((value) => ({ value, label: translate(t, DEPOSIT_STATE_LABEL_KEYS[value]) }))}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_FINANCE_LOAD_ERROR)} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<IconShieldLock size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_DEPOSITS_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_DEPOSITS_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminDepositRow>
          rowKey="bookingId"
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(row) => ({
            key: row.bookingId,
            title: row.machineryTitle,
            subtitle: `${row.hostName} → ${row.guestName}`,
            badge: renderCoverage(row),
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_DEPOSIT_AMOUNT), value: row.amount != null ? `${row.amount.toFixed(2)} €` : '—' },
              { label: translate(t, TRANSLATIONS.ADMIN_DEPOSIT_STATUS), value: row.status },
              {
                label: translate(t, row.holdExpired ? TRANSLATIONS.ADMIN_DEPOSIT_HOLD_EXPIRED : TRANSLATIONS.ADMIN_DEPOSIT_HOLD_EXPIRES),
                value: renderExpiry(row),
                wide: true,
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_DEPOSITS_COL_RENTAL),
                value: `${new Date(row.startAt).toLocaleDateString()} → ${new Date(row.endAt).toLocaleDateString()}`,
                wide: true,
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_DEPOSITS_FAILED),
                value: row.authorizationFailed ? <IconAlertTriangle size={16} color={CORAL} /> : undefined,
              },
            ],
            actions: renderActions(row),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_SPACE),
              dataIndex: 'machineryTitle',
              key: 'machineryTitle',
              sorter: textSorter<IAdminDepositRow>('machineryTitle'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_GUEST),
              dataIndex: 'guestName',
              key: 'guestName',
              sorter: textSorter<IAdminDepositRow>('guestName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_DEPOSIT_AMOUNT),
              dataIndex: 'amount',
              key: 'amount',
              sorter: numberSorter<IAdminDepositRow>('amount'),
              render: (value?: number) => (value != null ? `${value.toFixed(2)} €` : '—'),
            },
            { title: translate(t, TRANSLATIONS.ADMIN_DEPOSIT_STATUS), dataIndex: 'status', key: 'status' },
            {
              title: translate(t, TRANSLATIONS.ADMIN_DEPOSIT_COVERAGE),
              key: 'coverage',
              render: (_: unknown, row: IAdminDepositRow) => renderCoverage(row),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_DEPOSIT_HOLD_EXPIRES),
              dataIndex: 'holdExpiresAt',
              key: 'holdExpiresAt',
              sorter: dateSorter<IAdminDepositRow>('holdExpiresAt'),
              render: (_: unknown, row: IAdminDepositRow) => renderExpiry(row),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_DEPOSITS_COL_RENTAL),
              key: 'rental',
              sorter: dateSorter<IAdminDepositRow>('startAt'),
              render: (_: unknown, row: IAdminDepositRow) =>
                `${new Date(row.startAt).toLocaleDateString()} → ${new Date(row.endAt).toLocaleDateString()}`,
            },
            { title: '', key: 'actions', render: (_: unknown, row: IAdminDepositRow) => renderActions(row) },
          ]}
        />
      )}

      <BookingMoneyDrawer bookingId={moneyBookingId} onClose={() => setMoneyBookingId(undefined)} />
    </div>
  );
};

export default DepositsTab;
